import { useRef, type ComponentType, type MouseEvent, type ReactNode, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollCue } from '@/hooks/useScrollCue';
import { RevcallLogo } from '@/components/common/RevcallLogo';
import { BRAND } from '@/config/brand';

/**
 * The shell's icon rail (W1, F1), shared by every space behind the
 * ui_refonte flag. Folded it is 56 px of icons; every entry keeps an
 * accessible name and a tooltip, the XDial mark stays visible (DES-21), and
 * the current page is announced with aria-current. What a space puts in it
 * (entries, primary action, client switcher, footer tools) comes from the
 * space; the rail only lays it out.
 */
/** DESIGN.md §2 focus ring: 2 px --vf-accent outline, 2 px offset. */
export const RAIL_FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vf-accent)]';

type Icon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

export interface RailItem {
  id: string;
  label: string;
  icon: Icon;
  active?: boolean;
  badge?: number;
  onSelect: () => void;
  /**
   * The entry's page (QA-W4-8): given, the entry is a link (new tab, middle
   * click, copy the address) and a plain click still calls onSelect.
   */
  href?: string;
  /** Shown under the entry when it is active and the rail is open (e.g. settings sections). */
  children?: ReactNode;
  tourId?: string;
}

export interface RailAction {
  label: string;
  icon: Icon;
  onSelect: () => void;
}

interface Props {
  ariaLabel: string;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onBrandClick?: () => void;
  primary?: RailAction;
  secondary?: RailAction;
  header?: (open: boolean) => ReactNode;
  groups: RailItem[][];
  footer?: (open: boolean) => ReactNode;
  /** False in the mobile drawer: no fold button, and room for the drawer's close button. */
  collapsible?: boolean;
}

export function AppRail({ ariaLabel, collapsed, onCollapsedChange, onBrandClick, primary, secondary, header, groups, footer, collapsible = true }: Props) {
  const { t } = useTranslation(['common']);
  const open = !collapsed;
  const listRef = useRef<HTMLDivElement>(null);
  const activeId = groups.flat().find((i) => i.active)?.id;
  const moreBelow = useScrollCue(listRef, [open, activeId]);
  const followLink = (item: RailItem) => (e: MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    item.onSelect();
  };
  return (
    <nav
      aria-label={ariaLabel}
      data-testid="app-rail"
      className="flex h-screen flex-shrink-0 flex-col border-r transition-[width] duration-150 motion-reduce:transition-none"
      style={{ width: open ? 224 : 56, background: 'var(--vf-surface-1)', borderColor: 'var(--vf-border)', padding: open ? '12px 10px' : '12px 6px', gap: 4 }}
    >
      <div className="flex items-center" style={{ gap: 8, marginBottom: 6, padding: collapsible ? '2px 4px' : '2px 40px 2px 4px', justifyContent: open ? 'flex-start' : 'center' }}>
        <button type="button" onClick={onBrandClick} aria-label={BRAND.name} className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] ${RAIL_FOCUS}`} style={{ background: 'var(--vf-accent-bg)' }}>
          <RevcallLogo size={16} color="var(--vf-accent)" />
        </button>
        {open && <span className="flex-1 text-[14px] font-bold" style={{ color: 'var(--vf-text-1)' }}>{BRAND.name}</span>}
      </div>
      {collapsible && <button
        type="button"
        onClick={() => onCollapsedChange(!collapsed)}
        aria-expanded={open}
        aria-label={open ? t('common:rail.collapse') : t('common:rail.expand')}
        title={open ? t('common:rail.collapse') : t('common:rail.expand')}
        className={`mb-1 flex h-8 items-center justify-center rounded-md text-[12px] ${RAIL_FOCUS}`}
        style={{ color: 'var(--vf-text-3)', border: '1px solid var(--vf-border)', background: 'var(--vf-bg)' }}
      >
        {open ? <ChevronLeft size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
      </button>}

      {primary && (
        <button
          type="button"
          onClick={primary.onSelect}
          aria-label={open ? undefined : primary.label}
          title={open ? undefined : primary.label}
          className={`flex min-h-[40px] items-center rounded-lg text-[13px] font-semibold ${RAIL_FOCUS}`}
          style={{ background: 'var(--vf-primary)', color: 'var(--vf-primary-fg)', gap: 8, padding: open ? '0 12px' : 0, justifyContent: open ? 'flex-start' : 'center' }}
        >
          <primary.icon size={16} aria-hidden />
          {open && <span>{primary.label}</span>}
        </button>
      )}
      {secondary && (
        <button
          type="button"
          onClick={secondary.onSelect}
          aria-label={open ? undefined : secondary.label}
          title={open ? undefined : secondary.label}
          className={`flex min-h-[36px] items-center rounded-lg text-[13px] font-medium ${RAIL_FOCUS}`}
          style={{ border: '1px solid var(--vf-border)', color: 'var(--vf-text-1)', gap: 8, padding: open ? '0 12px' : 0, justifyContent: open ? 'flex-start' : 'center' }}
        >
          <secondary.icon size={16} aria-hidden />
          {open && <span>{secondary.label}</span>}
        </button>
      )}

      {header?.(open)}

      <div ref={listRef} className="relative flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ gap: 2 }}>
        {groups.filter((g) => g.length > 0).map((group, gi) => (
          <ul key={gi} className="flex flex-col" style={{ gap: 2, borderTop: gi > 0 ? '1px solid var(--vf-border)' : undefined, paddingTop: gi > 0 ? 6 : 0, marginTop: gi > 0 ? 4 : 0 }}>
            {group.map((item) => (
              <li key={item.id}>
                <RailEntry
                  href={item.href}
                  onClick={item.href ? followLink(item) : item.onSelect}
                  aria-current={item.active ? 'page' : undefined}
                  aria-label={open ? undefined : item.label}
                  title={open ? undefined : item.label}
                  data-tour={item.tourId}
                  className={`relative flex min-h-[36px] w-full items-center rounded-lg text-[13px] ${RAIL_FOCUS}`}
                  style={{
                    gap: 10,
                    padding: open ? '0 10px' : 0,
                    justifyContent: open ? 'flex-start' : 'center',
                    background: item.active ? 'var(--vf-surface-3)' : 'transparent',
                    color: item.active ? 'var(--vf-text-1)' : 'var(--vf-text-2)',
                    fontWeight: item.active ? 600 : 500,
                  }}
                >
                  {item.active && <span aria-hidden className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full" style={{ background: 'var(--vf-accent)' }} />}
                  <item.icon size={16} aria-hidden />
                  {open && <span className="flex-1 text-left">{item.label}</span>}
                  {typeof item.badge === 'number' && item.badge > 0 && (
                    <span className={`tabular-nums text-[11px] font-semibold ${open ? '' : 'absolute -right-0.5 -top-0.5'}`} style={{ color: 'var(--vf-text-2)', background: 'var(--vf-surface-3)', borderRadius: 999, padding: '0 6px', lineHeight: '16px' }}>
                      {item.badge}
                    </span>
                  )}
                </RailEntry>
                {item.active && open && item.children}
              </li>
            ))}
          </ul>
        ))}
        {/* QA-W4-8: entries below the fold (settings sections at 900 px
            high) were hidden under the footer cards with no hint. */}
        {moreBelow && (
          <div aria-hidden data-testid="rail-more-below" className="pointer-events-none sticky bottom-0 -mt-6 flex h-6 shrink-0 items-end justify-center" style={{ background: 'linear-gradient(to bottom, transparent, var(--vf-surface-1))' }}>
            <ChevronDown size={12} style={{ color: 'var(--vf-text-3)' }} />
          </div>
        )}
      </div>

      {footer?.(open)}
    </nav>
  );
}

/** A rail entry: a link when it has a page, a button otherwise. */
function RailEntry({ href, children, ...rest }: { href?: string; children: ReactNode; onClick: (e: MouseEvent) => void } & Record<string, unknown>) {
  if (href) return <a href={href} {...rest}>{children}</a>;
  return <button type="button" {...rest}>{children}</button>;
}
